<template>
  <div>
    <h2>{{ templateName }} Template</h2>
    <TextAreaInput v-model="templateString" startHeight="10rem" ref="textBox" />
    <button
      v-for="token of availableTokens"
      v-bind:key="token"
      class="btn btn-primary p-1 m-1"
      @click="insertToken(token)"
    >
      {{ token }}
    </button>
    <div v-if="sampleString">
      <h4>Preview:</h4>
      <span class="preview-string mx-2">{{ sampleString }}</span>
    </div>
    <div class="d-flex justify-content-between">
      <button class="btn btn-success" @click="testPrint">Test Print</button>
      <SubmitCancelRow @submit="handleSubmit" @cancel="handleCancel" />
    </div>
  </div>
</template>

<script>
import { now } from "moment";
import TextAreaInput from "../TextAreaInput.vue";
import SubmitCancelRow from "./SubmitCancelRow.vue";
import { formatDate, replaceTokens } from "@/modules/printFileDrop";
import { handleErrors } from "@/modules/handleErrors";

export default {
  props: [
    "templateName",
    "originalString",
    "trayOptions",
    "defaultTray",
    "printerName",
    "settings"
  ],
  components: { TextAreaInput, SubmitCancelRow },
  data() {
    return {
      templateString: "",
      cassetteTokens: [
        "CasePrefixAndYear",
        "CaseNumSeq",
        "SpecimenOrder",
        "CassetteId",
        "PipeDelimitedConcat",
        "PatientLastName",
        "PatientFirstName",
        "patientFirstInitial",
        "patientLastInitial",
        "DOB",
        "MRN",
        "CollectedOn",
        "TrayName",
        "procedure1",
        "procedure2",
        "priority",
        "protocol",
        "denoteSingleSpecimen",
        "denoteSingleBlock"
      ],
      slideTokens: [
        "CasePrefixAndYear",
        "CaseNumSeq",
        "SpecimenOrder",
        "CassetteId",
        "SlideNumber",
        "SlideLabelDescription",
        "Site",
        "PipeDelimitedConcat",
        "PatientLastName",
        "PatientFirstName",
        "patientFirstInitial",
        "patientLastInitial",
        "MRN",
        "ProviderFirstName",
        "ProviderLastName",
        "HasHistory",
        "CollectedOn",
        "TrayName",
        "denoteSingleSpecimen",
        "denoteSingleBlock"
      ]
    };
  },
  mounted() {
    this.templateString = this.originalString || "";
  },
  computed: {
    sampleData() {
      const currentYear = new Date().getFullYear() + "";
      const twoDigitYear = currentYear.substr(2);
      const trayName = this.defaultTray || (this.trayOptions?.length ? this.trayOptions[0] : "T1");
      let protocol = "Excision";
      let procedure1 = "P1";
      let procedure2 = "P2";
      let priority = "RUSH";
      const { printProtocols, priorities, printProcedure1, printProcedure2 } = this.settings;
      if (printProtocols?.length) {
        protocol = printProtocols[0].protocolPrintText;
      }
      if (printProcedure1) {
        procedure1 = printProcedure1;
      }
      if (printProcedure2) {
        procedure2 = printProcedure2;
      }
      if (priorities?.length) {
        priority = priorities[0];
      }
      return {
        CasePrefixAndYear: "T" + twoDigitYear,
        CaseNumSeq: "12345",
        SpecimenOrder: "A",
        CassetteId: "1",
        SlideNumber: "1",
        SlideLabelDescription: "H&E",
        Site: "Arm",
        PipeDelimitedConcat: `T${twoDigitYear}12345|A|1`,
        PatientLastName: "Smith",
        PatientFirstName: "John",
        PatientLastInitial: "S",
        PatientFirstInitial: "J",
        DOB: "12/25/1970",
        MRN: "ABC1234",
        ProviderFirstName: "Jane",
        ProviderLastName: "Brown",
        HasHistory: "Y",
        CollectedOn: formatDate(now()),
        TrayName: trayName,
        denoteSingleSpecimen: "Y",
        denoteSingleBlock: "Y",
        procedure1,
        procedure2,
        priority,
        protocol
      };
    },
    sampleString() {
      return replaceTokens(this.sampleData, this.templateString);
    },
    availableTokens() {
      if (/slide/i.test(this.templateName)) {
        return this.slideTokens;
      }
      return this.cassetteTokens;
    }
  },
  methods: {
    handleSubmit() {
      this.$emit("submit", this.templateString);
      this.$emit("close");
    },
    handleCancel() {
      this.$emit("close");
    },
    insertToken(token) {
      const textBox = this.$refs.textBox?.$el;
      const [el] = textBox.getElementsByTagName("textarea");
      if (el) {
        const { selectionStart, selectionEnd } = el;
        const newSelection = selectionStart + token.length + 2;
        const prefix = this.templateString.slice(0, selectionStart);
        const suffix = this.templateString.slice(selectionEnd, this.templateString.length);
        this.templateString = `${prefix}{${token}}${suffix}`;
        this.$nextTick(() => {
          el.focus();
          el.setSelectionRange(newSelection, newSelection);
        });
      }
    },
    async testPrint() {
      const payloadObject = {
        PrinterName: this.printerName,
        Items: [{ stringToPrint: this.sampleString }]
      };
      try {
        await fetch(this.settings.url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(payloadObject)
        });
        window.notify("Print request sent successfully.");
      } catch (error) {
        handleErrors(error);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.preview-string {
  font-size: 2em;
  font-family: "Courier New", Courier, monospace;
}
</style>
